import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

interface UseStepHistoryOptions {
  step: number;
  onBack: () => void;
  enabled: boolean;
}

const useStepHistory = ({ step, onBack, enabled }: UseStepHistoryOptions) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const stepRef = useRef(step);
  const onBackRef = useRef(onBack);
  const isProgrammaticRef = useRef(false);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  // When step advances forward, push URL with step param
  useEffect(() => {
    if (step > stepRef.current) {
      isProgrammaticRef.current = true;
      const params = new URLSearchParams(searchParams);
      params.set('step', String(step));
      navigate(`?${params.toString()}`);
    }
    stepRef.current = step;
  }, [step]);

  // On back navigation (URL changes), sync step from URL
  useEffect(() => {
    if (isProgrammaticRef.current) {
      isProgrammaticRef.current = false;
      return;
    }

    if (!enabled) return;

    const stepParam = searchParams.get('step');
    const urlStep = stepParam !== null ? parseInt(stepParam) : 0;

    if (urlStep < stepRef.current) {
      onBackRef.current();
    }
  }, [location.key]);

  // On mount, restore URL from localStorage if needed
  useEffect(() => {
    if (step > 0) {
      const stepParam = searchParams.get('step');
      if (stepParam === null || parseInt(stepParam) !== step) {
        const params = new URLSearchParams(searchParams);
        params.set('step', String(step));
        navigate(`?${params.toString()}`, { replace: true });
      }
    }
  }, []);

  return { goBack: () => navigate(-1) };
};

export default useStepHistory;
